import React, { useState, useEffect } from 'react';
import {
  Users,
  Brain,
  CheckCircle,
  Loader2,
  Settings,
  Clock,
  Trash2,
  ArrowRight,
} from 'lucide-react';
import './MassAIPowerUserAction.css';
import { AdminClientApi } from '../Admin-ClientLibrary';
import {
  GetUsersInputInterface,
  GetThoughtsInputInterface,
  ClientUserInterface,
  ClientThoughtWithAuthor,
  ClientFeed,
} from '../Admin-ClientLibrary';
import { getApiUrl } from '../config/api';
import { useRecentEntities } from '../contexts/RecentEntitiesContext';
import { RecentEntity } from '../types/RecentEntities';

const MassAIPowerUserAction: React.FC = () => {
  const { recentEntities } = useRecentEntities();
  const [step, setStep] = useState<'users' | 'thoughts' | 'simulation' | 'ready' | 'processing'>(
    'users'
  );
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [openRecentDropdown, setOpenRecentDropdown] = useState<string | null>(null);

  // Processing state
  const [currentThoughtIndex, setCurrentThoughtIndex] = useState(0);
  const [currentUserIndex, setCurrentUserIndex] = useState(0);
  const [processingStage, setProcessingStage] = useState<
    | 'thought_intro'
    | 'user_intro'
    | 'position_decision'
    | 'checking_belief'
    | 'generating_belief_system'
    | 'showing_supporting_beliefs'
    | 'supporting_beliefs_decision'
    | 'reviewing_supporting_beliefs'
    | 'saving_beliefs'
    | 'processing_layers'
    | 'thought_complete'
    | 'checking_supporting_beliefs'
  >('thought_intro');
  const [currentLayer, setCurrentLayer] = useState(0);
  const [beliefPositionCounts, setBeliefPositionCounts] = useState({
    believe: 0,
    disbelieve: 0,
    unsure: 0,
  });
  const [currentBelief, setCurrentBelief] = useState<any>(null);
  const [currentBeliefSystem, setCurrentBeliefSystem] = useState<any>(null);
  const [currentSupportingBeliefs, setCurrentSupportingBeliefs] = useState<{
    existing: any[];
    new: any[];
  }>({ existing: [], new: [] });
  const [layerProcessingIndex, setLayerProcessingIndex] = useState(0);
  const [userWillTakePosition, setUserWillTakePosition] = useState(false);
  const [userWillAddSupportingBeliefs, setUserWillAddSupportingBeliefs] = useState(false);
  const [autoMode, setAutoMode] = useState(false);
  const [isAutoProcessing, setIsAutoProcessing] = useState(false);
  const [autoAcceptSupportingBeliefs, setAutoAcceptSupportingBeliefs] = useState(false);

  // User-related state
  const [goeUserId, setGoeUserId] = useState<string>('11');
  const [loeUserId, setLoeUserId] = useState<string>('22');
  const [retrievedUsers, setRetrievedUsers] = useState<ClientUserInterface[]>([]);
  const [usersRetrieved, setUsersRetrieved] = useState(false);

  // Thought-related state
  const [thoughtSearch, setThoughtSearch] = useState('');
  const [thoughtCreatedAfter, setThoughtCreatedAfter] = useState('');
  const [thoughtCreatedBefore, setThoughtCreatedBefore] = useState('');
  const [thoughtLimit, setThoughtLimit] = useState<string>('50');
  const [isInFeed, setIsInFeed] = useState<string>('');
  const [retrievedThoughts, setRetrievedThoughts] = useState<ClientThoughtWithAuthor[]>([]);
  const [thoughtsRetrieved, setThoughtsRetrieved] = useState(false);

  // Simulation parameters state
  const [positionPercentage, setPositionPercentage] = useState<string>('75');
  const [supportingBeliefsPercentage, setSupportingBeliefsPercentage] = useState<string>('50');
  const [contrarianAfterMatches, setContrarianAfterMatches] = useState<string>('5');
  const [simulationConfigured, setSimulationConfigured] = useState(false);

  const client = new AdminClientApi(getApiUrl(), () => '');

  // Auto processing effect
  useEffect(() => {
    if (autoMode && isAutoProcessing && !loading && step === 'processing') {
      const timer = setTimeout(() => {
        setIsAutoProcessing(false);
        handleProcessingNext();
      }, 500); // Delay to allow UI updates

      return () => clearTimeout(timer);
    }
  }, [autoMode, isAutoProcessing, loading, step, processingStage, autoAcceptSupportingBeliefs]);

  // Close recent dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.recent-dropdown-container')) {
        setOpenRecentDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Recent entities helpers
  const getFilteredRecentEntities = (
    type: 'user' | 'belief' | 'thought' | 'feed'
  ): RecentEntity[] => {
    return recentEntities.filter((entity) => entity.type === type);
  };

  const handleRecentEntityClick = (
    entity: RecentEntity,
    filterSetter: (value: string) => void
  ): void => {
    filterSetter(entity.id);
    setOpenRecentDropdown(null);
  };

  const toggleRecentDropdown = (dropdownType: string): void => {
    setOpenRecentDropdown(openRecentDropdown === dropdownType ? null : dropdownType);
  };

  const getEntityDisplayText = (entity: RecentEntity): string => {
    switch (entity.type) {
      case 'user':
        const user = entity.data as ClientUserInterface;
        return `${user.name || `User ${entity.id}`}`;
      case 'belief':
        return `Belief ${entity.id}`;
      case 'thought':
        const thought = entity.data as ClientThoughtWithAuthor;
        const text = thought.text || '';
        return text.length > 40 ? `${text.substring(0, 40)}...` : text;
      case 'feed':
        const feed = entity.data as ClientFeed;
        return feed.title || `Feed ${entity.id}`;
      default:
        return `Entity ${entity.id}`;
    }
  };

  const handleAcceptUsers = async () => {
    setLoading(true);
    setMessage('');

    try {
      const input: GetUsersInputInterface = {
        limit: 1000, // Get all power users
      };

      // Add user ID filters if specified
      if (goeUserId) {
        input.afterId = parseInt(goeUserId) - 1; // afterId is exclusive, so subtract 1
      }
      if (loeUserId) {
        input.beforeId = parseInt(loeUserId) + 1; // beforeId is exclusive, so add 1
      }

      const response = await client.getUsers(input);

      if (response.success) {
        // Filter users by ID range (additional safety check)
        const filteredUsers = response.users.filter((user) => {
          const userId = user.account_id;
          const goeFilter = !goeUserId || userId >= parseInt(goeUserId);
          const loeFilter = !loeUserId || userId <= parseInt(loeUserId);
          return goeFilter && loeFilter;
        });

        setRetrievedUsers(filteredUsers);
        setUsersRetrieved(true);
        setMessage('Users retrieved');
        setStep('thoughts');
      } else {
        setMessage(`Error: ${response.message}`);
      }
    } catch (error) {
      setMessage(
        `Error retrieving users: ${error instanceof Error ? error.message : String(error)}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptThoughts = async () => {
    setLoading(true);
    setMessage('');

    try {
      const input: GetThoughtsInputInterface = {
        limit: thoughtLimit ? parseInt(thoughtLimit) : 50,
      };

      if (thoughtSearch) {
        input.search = thoughtSearch;
      }
      if (thoughtCreatedAfter) {
        input.createdAfter = thoughtCreatedAfter;
      }
      if (thoughtCreatedBefore) {
        input.createdBefore = thoughtCreatedBefore;
      }
      if (isInFeed) {
        input.isInFeed = parseInt(isInFeed);
      }

      const response = await client.getThoughts(input);

      if (response.success) {
        setRetrievedThoughts(response.thoughts);
        setThoughtsRetrieved(true);
        setMessage('Thoughts retrieved');
        setStep('simulation');
      } else {
        setMessage(`Error: ${response.message}`);
      }
    } catch (error) {
      setMessage(
        `Error retrieving thoughts: ${error instanceof Error ? error.message : String(error)}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptSimulation = async () => {
    setLoading(true);
    setMessage('');

    try {
      // Validate input ranges
      const positionPct = parseInt(positionPercentage);
      const supportingPct = parseInt(supportingBeliefsPercentage);
      const contrarianNum = parseInt(contrarianAfterMatches);

      if (positionPct < 1 || positionPct > 100) {
        setMessage('Position percentage must be between 1 and 100');
        return;
      }
      if (supportingPct < 0 || supportingPct > 100) {
        setMessage('Supporting beliefs percentage must be between 0 and 100');
        return;
      }
      if (contrarianNum < 1) {
        setMessage('Contrarian position threshold must be at least 1');
        return;
      }

      // Simulate configuration acceptance (no API call needed yet)
      setSimulationConfigured(true);
      setMessage('Simulation parameters configured');
      setStep('ready');
    } catch (error) {
      setMessage(
        `Error configuring simulation: ${error instanceof Error ? error.message : String(error)}`
      );
    } finally {
      setLoading(false);
    }
  };

  const startProcessing = () => {
    setStep('processing');
    setCurrentThoughtIndex(0);
    setCurrentUserIndex(0);
    setProcessingStage('thought_intro');
    setBeliefPositionCounts({ believe: 0, disbelieve: 0, unsure: 0 });
    setCurrentLayer(0);
    setMessage('');
  };

  const calculateProbability = (percentage: number): boolean => {
    return Math.random() * 100 < percentage;
  };

  const getCurrentThought = () => {
    return retrievedThoughts[currentThoughtIndex];
  };

  const getCurrentUser = () => {
    return retrievedUsers[currentUserIndex];
  };

  const isContrarianNeeded = (): string | null => {
    const { believe, disbelieve, unsure } = beliefPositionCounts;
    const contrarianThreshold = parseInt(contrarianAfterMatches);

    // Check if believe is 0 and disbelieve >= threshold
    if (believe === 0 && disbelieve >= contrarianThreshold) {
      return 'believe';
    }
    // Check if disbelieve is 0 and believe >= threshold
    if (disbelieve === 0 && believe >= contrarianThreshold) {
      return 'disbelieve';
    }

    return null;
  };

  const removeExistingSupportingBelief = (index: number) => {
    const updated = { ...currentSupportingBeliefs };
    updated.existing.splice(index, 1);
    setCurrentSupportingBeliefs(updated);
  };

  const removeNewSupportingBelief = (index: number) => {
    const updated = { ...currentSupportingBeliefs };
    updated.new.splice(index, 1);
    setCurrentSupportingBeliefs(updated);
  };

  const toggleNewSupportingBeliefPosition = (index: number) => {
    const updated = { ...currentSupportingBeliefs };
    const belief = updated.new[index];

    // Cycle through positions: BELIEVE -> DISBELIEVE -> UNSURE -> BELIEVE
    switch (belief.position) {
      case 'BELIEVE':
        belief.position = 'DISBELIEVE';
        break;
      case 'DISBELIEVE':
        belief.position = 'UNSURE';
        break;
      case 'UNSURE':
        belief.position = 'BELIEVE';
        break;
      default:
        belief.position = 'BELIEVE';
    }

    setCurrentSupportingBeliefs(updated);
  };

  const handleProcessingNext = async () => {
    setLoading(true);

    try {
      const currentThought = getCurrentThought();
      const currentUser = getCurrentUser();

      if (!currentThought || !currentUser) {
        setMessage('Error: Missing thought or user data');
        return;
      }

      let nextStage: string | null = null;

      switch (processingStage) {
        case 'thought_intro':
          setProcessingStage('user_intro');
          nextStage = 'user_intro';
          break;

        case 'user_intro':
          // Calculate if user will take position based on probability
          const willTakePosition = calculateProbability(parseInt(positionPercentage));
          setUserWillTakePosition(willTakePosition);
          setProcessingStage('position_decision');
          nextStage = 'position_decision';
          break;

        case 'position_decision':
          if (!userWillTakePosition) {
            // Move to next user
            if (currentUserIndex < retrievedUsers.length - 1) {
              setCurrentUserIndex(currentUserIndex + 1);
              setProcessingStage('user_intro');
              nextStage = 'user_intro';
            } else {
              // Move to next thought
              if (currentThoughtIndex < retrievedThoughts.length - 1) {
                setCurrentThoughtIndex(currentThoughtIndex + 1);
                setCurrentUserIndex(0);
                setProcessingStage('thought_complete');
                nextStage = 'thought_complete';
              } else {
                // All processing complete
                setMessage('All thoughts processed successfully!');
                setStep('ready');
                return;
              }
            }
          } else {
            setProcessingStage('checking_belief');
            nextStage = 'checking_belief';
          }
          break;

        case 'checking_belief': {
          // Check if user has existing belief on this thought
          const beliefResponse = await client.getBeliefs({
            userId: currentUser.account_id,
            thoughtId: currentThought.content_id,
            limit: 1,
          });

          if (beliefResponse.success && beliefResponse.beliefs.length > 0) {
            const existingBelief = beliefResponse.beliefs[0];
            setCurrentBelief(existingBelief);
            setProcessingStage('checking_supporting_beliefs');
            nextStage = 'checking_supporting_beliefs';
          } else {
            // Check if contrarian position is needed
            const contrarianPosition = isContrarianNeeded();
            if (contrarianPosition) {
              // Create contrarian belief using the admin API
              setMessage(`Creating contrarian ${contrarianPosition} position...`);

              try {
                const createResponse = await client.createAdminBelief({
                  userId: currentUser.account_id,
                  thoughtId: currentThought.content_id,
                  position: contrarianPosition.toLowerCase() as 'believe' | 'disbelieve' | 'unsure',
                });

                if (createResponse.success && createResponse.belief) {
                  setCurrentBelief(createResponse.belief);
                  setProcessingStage('generating_belief_system');
                  nextStage = 'generating_belief_system';
                } else {
                  setMessage(`Error creating contrarian belief: ${createResponse.message}`);
                  return;
                }
              } catch (error) {
                setMessage(
                  `Error creating contrarian belief: ${
                    error instanceof Error ? error.message : String(error)
                  }`
                );
                return;
              }
            } else {
              // Generate new belief and robust belief system
              try {
                const generateResponse =
                  await client.generateBeliefAndRobustBeliefSystemFromThought({
                    thoughtId: currentThought.content_id,
                    userId: currentUser.account_id,
                  });

                if (generateResponse.success) {
                  setCurrentBelief(generateResponse.belief);
                  setCurrentBeliefSystem(generateResponse.robust_belief_system);
                  setProcessingStage('showing_supporting_beliefs');
                  nextStage = 'showing_supporting_beliefs';
                } else {
                  setMessage(`Error generating belief: ${generateResponse.message}`);
                  return;
                }
              } catch (error) {
                setMessage(
                  `Error: API not yet implemented - generateBeliefAndRobustBeliefSystemFromThought`
                );
                return;
              }
            }
          }
          break;
        }

        case 'checking_supporting_beliefs': {
          // User must click Next to proceed
          // On Next, check supporting belief count
          const countResponse = await client.getSupportingBeliefCountFromBeliefs({
            beliefIds: [currentBelief.id],
          });
          if (
            countResponse.success &&
            countResponse.supportingBeliefCounts &&
            countResponse.supportingBeliefCounts.length > 0 &&
            countResponse.supportingBeliefCounts[0].supportingBeliefCount > 0
          ) {
            setMessage('The user has supporting beliefs, skipping to the next user.');
            // Update belief position counts
            const position = currentBelief.position.toLowerCase();
            setBeliefPositionCounts((prev) => ({
              ...prev,
              [position]: prev[position as keyof typeof prev] + 1,
            }));
            // Move to next user or thought
            if (currentUserIndex < retrievedUsers.length - 1) {
              setCurrentUserIndex(currentUserIndex + 1);
              setProcessingStage('user_intro');
              nextStage = 'user_intro';
            } else {
              if (currentThoughtIndex < retrievedThoughts.length - 1) {
                setCurrentThoughtIndex(currentThoughtIndex + 1);
                setCurrentUserIndex(0);
                setProcessingStage('thought_complete');
                nextStage = 'thought_complete';
              } else {
                setMessage('All thoughts processed successfully!');
                setStep('ready');
                return;
              }
            }
          } else {
            setMessage('The user does not have supporting beliefs, proceeding...');
            setProcessingStage('generating_belief_system');
            nextStage = 'generating_belief_system';
          }
          break;
        }

        case 'generating_belief_system':
          if (currentBelief) {
            const beliefSystemResponse = await client.generateRobustBeliefSystem({
              beliefId: currentBelief.id,
            });

            if (beliefSystemResponse.success) {
              setCurrentBeliefSystem(beliefSystemResponse.robust_belief_system);
              setProcessingStage('showing_supporting_beliefs');
              nextStage = 'showing_supporting_beliefs';
            } else {
              setMessage(`Error generating belief system: ${beliefSystemResponse.message}`);
              return;
            }
          }
          break;

        case 'showing_supporting_beliefs':
          if (currentBeliefSystem && currentBelief) {
            const supportingResponse = await client.getSupportingBeliefsFromBeliefSystem({
              beliefId: currentBelief.id,
              beliefSystem: currentBeliefSystem,
            });

            if (supportingResponse.success && supportingResponse.result) {
              setCurrentSupportingBeliefs({
                existing: supportingResponse.result.existing,
                new: supportingResponse.result.new,
              });
            }
          }
          setProcessingStage('supporting_beliefs_decision');
          nextStage = 'supporting_beliefs_decision';
          break;

        case 'supporting_beliefs_decision': {
          // Calculate if user will add supporting beliefs based on probability
          const willAddSupportingBeliefs = calculateProbability(
            parseInt(supportingBeliefsPercentage)
          );
          setUserWillAddSupportingBeliefs(willAddSupportingBeliefs);

          if (willAddSupportingBeliefs) {
            setProcessingStage('reviewing_supporting_beliefs');
            nextStage = 'reviewing_supporting_beliefs';
          } else {
            // Skip supporting beliefs and go directly to saving
            setCurrentSupportingBeliefs({ existing: [], new: [] });
            setProcessingStage('saving_beliefs');
            nextStage = 'saving_beliefs';
          }
          break;
        }

        case 'reviewing_supporting_beliefs':
          // User reviews and edits supporting beliefs
          // This stage is handled in the UI, no API calls needed
          setProcessingStage('saving_beliefs');
          nextStage = 'saving_beliefs';
          break;

        case 'saving_beliefs': {
          // Save supporting beliefs to database
          const totalBeliefs =
            currentSupportingBeliefs.existing.length + currentSupportingBeliefs.new.length;

          if (totalBeliefs > 0) {
            setMessage(`Saving ${totalBeliefs} supporting beliefs...`);

            try {
              // Save existing supporting beliefs (link to existing beliefs)
              for (const supportingBelief of currentSupportingBeliefs.existing) {
                const addResponse = await client.addSupportingBelief({
                  beliefId: currentBelief.id,
                  supportingBeliefId: supportingBelief.belief_id!,
                  userId: currentUser.account_id,
                });

                if (!addResponse.success) {
                  setMessage(`Error linking existing supporting belief: ${addResponse.message}`);
                  return;
                }
              }

              // Save new supporting beliefs (create new thoughts and beliefs)
              for (const supportingBelief of currentSupportingBeliefs.new) {
                const saveResponse = await client.saveNewSupportingThoughtAndBelief({
                  userId: currentUser.account_id,
                  beliefId: currentBelief.id,
                  newThoughtText: supportingBelief.text,
                  supportingBeliefPosition: supportingBelief.position,
                });

                if (!saveResponse.success) {
                  setMessage(`Error saving new supporting belief: ${saveResponse.message}`);
                  return;
                }
              }

              setMessage(`Successfully saved ${totalBeliefs} supporting beliefs`);
            } catch (error) {
              setMessage(
                `Error saving supporting beliefs: ${
                  error instanceof Error ? error.message : String(error)
                }`
              );
              return;
            }
          } else {
            setMessage('No supporting beliefs to save');
          }

          // Update belief position counts
          if (currentBelief) {
            const position = currentBelief.position.toLowerCase();
            setBeliefPositionCounts((prev) => ({
              ...prev,
              [position]: prev[position as keyof typeof prev] + 1,
            }));
          }

          // Move to next user or thought
          if (currentUserIndex < retrievedUsers.length - 1) {
            setCurrentUserIndex(currentUserIndex + 1);
            setProcessingStage('user_intro');
            nextStage = 'user_intro';
          } else {
            if (currentThoughtIndex < retrievedThoughts.length - 1) {
              setCurrentThoughtIndex(currentThoughtIndex + 1);
              setCurrentUserIndex(0);
              setProcessingStage('thought_complete');
              nextStage = 'thought_complete';
            } else {
              setMessage('All thoughts processed successfully!');
              setStep('ready');
              return;
            }
          }
          break;
        }
        case 'thought_complete':
          // Reset for next thought
          setBeliefPositionCounts({ believe: 0, disbelieve: 0, unsure: 0 });
          setProcessingStage('thought_intro');
          nextStage = 'thought_intro';
          break;

        default:
          break;
      }

      // Auto mode: automatically proceed unless reviewing_supporting_beliefs (unless auto-accept is enabled)
      if (
        autoMode &&
        nextStage &&
        (nextStage !== 'reviewing_supporting_beliefs' || autoAcceptSupportingBeliefs)
      ) {
        setIsAutoProcessing(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const getProcessingMessage = (): string => {
    const currentThought = getCurrentThought();
    const currentUser = getCurrentUser();

    switch (processingStage) {
      case 'thought_intro':
        return `Processing thought: "${currentThought?.text || 'Unknown'}"`;
      case 'user_intro':
        return `Processing user: ${currentUser?.name || 'Unknown'}...`;
      case 'position_decision':
        return `Based on the probability settings, this user ${
          userWillTakePosition ? 'will' : 'will not'
        } take a position on this thought.`;
      case 'checking_belief':
        return 'Checking to see if user has a belief on this thought...';
      case 'generating_belief_system':
        return currentBelief
          ? `User already has belief: ${currentBelief.position}, generating robust belief system...`
          : 'User does not have a belief on this thought. Generating a belief and robust belief system...';
      case 'showing_supporting_beliefs':
        return currentBelief && currentBeliefSystem
          ? `User has this belief: ${
              currentBelief.position
            } and this belief system: ${currentBeliefSystem.belief_system.substring(
              0,
              100
            )}... Generating supporting beliefs...`
          : 'Generating supporting beliefs...';
      case 'supporting_beliefs_decision':
        return `Based on the probability settings, this user ${
          userWillAddSupportingBeliefs ? 'will' : 'will not'
        } add supporting beliefs.`;
      case 'reviewing_supporting_beliefs':
        const totalBeliefs =
          currentSupportingBeliefs.existing.length + currentSupportingBeliefs.new.length;
        return `Review and edit supporting beliefs (${totalBeliefs} beliefs found)`;
      case 'saving_beliefs':
        return 'Saving supporting beliefs...';
      case 'processing_layers':
        return 'For each supporting belief, I will now use the probabilities selected to determine if the user will add supporting beliefs to these supporting beliefs.';
      case 'thought_complete':
        return 'Thought complete, moving on to next thought...';
      case 'checking_supporting_beliefs':
        return 'Checking if user already has supporting beliefs...';
      default:
        return 'Processing...';
    }
  };

  // RecentDropdown component
  const RecentDropdown: React.FC<{
    type: 'user' | 'belief' | 'thought' | 'feed';
    dropdownKey: string;
    filterSetter: (value: string) => void;
  }> = ({ type, dropdownKey, filterSetter }) => {
    const filteredEntities = getFilteredRecentEntities(type);
    const isOpen = openRecentDropdown === dropdownKey;

    return (
      <div className="recent-dropdown-container">
        <button
          type="button"
          className="recent-button"
          onClick={() => toggleRecentDropdown(dropdownKey)}
          disabled={filteredEntities.length === 0}
        >
          <Clock size={14} />
          Recent
        </button>
        {isOpen && filteredEntities.length > 0 && (
          <div className="recent-dropdown">
            <div className="recent-dropdown-header">
              Recent{' '}
              {type === 'user'
                ? 'Users'
                : type === 'belief'
                ? 'Beliefs'
                : type === 'thought'
                ? 'Thoughts'
                : 'Feeds'}
            </div>
            <ul className="recent-dropdown-list">
              {filteredEntities.slice(0, 5).map((entity) => (
                <li
                  key={`${entity.type}-${entity.id}`}
                  className="recent-dropdown-item"
                  onClick={() => handleRecentEntityClick(entity, filterSetter)}
                >
                  <span className="recent-entity-id">ID: {entity.id}</span>
                  <span className="recent-entity-text">{getEntityDisplayText(entity)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mass-ai-power-user-page">
      <div className="page-header">
        <div className="header-title">
          <Settings size={32} />
          <h1>Mass AI Power User Action</h1>
        </div>
        <p className="header-description">
          Configure AI actions for Power Users and selected thoughts.
        </p>
      </div>

      <div className="steps-container">
        {/* Step 1: Users */}
        <div
          className={`step-card ${
            step === 'users' ? 'active' : usersRetrieved ? 'completed' : 'inactive'
          }`}
        >
          <div className="step-header">
            <div className="step-icon">
              {usersRetrieved ? <CheckCircle size={24} /> : <Users size={24} />}
            </div>
            <h2>Step 1: Users to simulate with AI</h2>
          </div>

          <div className="step-content">
            <div className="user-filters">
              <div className="filter-group">
                <label>Greater than or equal to User ID:</label>
                <input
                  type="number"
                  value={goeUserId}
                  onChange={(e) => setGoeUserId(e.target.value)}
                  disabled={usersRetrieved}
                  placeholder="11"
                />
              </div>
              <div className="filter-group">
                <label>Less than or equal to User ID:</label>
                <input
                  type="number"
                  value={loeUserId}
                  onChange={(e) => setLoeUserId(e.target.value)}
                  disabled={usersRetrieved}
                  placeholder="22"
                />
              </div>
            </div>

            {!usersRetrieved && (
              <button
                className="btn-primary accept-btn"
                onClick={handleAcceptUsers}
                disabled={loading}
              >
                {loading ? <Loader2 className="spinning" size={16} /> : null}
                Accept
              </button>
            )}

            {usersRetrieved && (
              <div className="success-message">
                <CheckCircle size={16} />
                <span>Users retrieved ({retrievedUsers.length} users)</span>
              </div>
            )}
          </div>
        </div>

        {/* Step 2: Thoughts */}
        <div
          className={`step-card ${
            step === 'thoughts' ? 'active' : thoughtsRetrieved ? 'completed' : 'inactive'
          }`}
        >
          <div className="step-header">
            <div className="step-icon">
              {thoughtsRetrieved ? <CheckCircle size={24} /> : <Brain size={24} />}
            </div>
            <h2>Step 2: Thoughts to process with AI</h2>
          </div>

          <div className="step-content">
            <div className="thought-filters">
              <div className="filter-group">
                <label>Search in thought text:</label>
                <input
                  type="text"
                  value={thoughtSearch}
                  onChange={(e) => setThoughtSearch(e.target.value)}
                  disabled={thoughtsRetrieved || step !== 'thoughts'}
                  placeholder="Search thoughts..."
                />
              </div>
              <div className="filter-group">
                <label>Created after:</label>
                <input
                  type="date"
                  value={thoughtCreatedAfter}
                  onChange={(e) => setThoughtCreatedAfter(e.target.value)}
                  disabled={thoughtsRetrieved || step !== 'thoughts'}
                />
              </div>
              <div className="filter-group">
                <label>Created before:</label>
                <input
                  type="date"
                  value={thoughtCreatedBefore}
                  onChange={(e) => setThoughtCreatedBefore(e.target.value)}
                  disabled={thoughtsRetrieved || step !== 'thoughts'}
                />
              </div>
              <div className="filter-group">
                <label>Limit:</label>
                <input
                  type="number"
                  value={thoughtLimit}
                  onChange={(e) => setThoughtLimit(e.target.value)}
                  disabled={thoughtsRetrieved || step !== 'thoughts'}
                  placeholder="50"
                />
              </div>
              <div className="filter-group">
                <label>In Feed ID:</label>
                <input
                  type="number"
                  value={isInFeed}
                  onChange={(e) => setIsInFeed(e.target.value)}
                  disabled={thoughtsRetrieved || step !== 'thoughts'}
                  placeholder="Enter Feed ID"
                />
                <RecentDropdown type="feed" dropdownKey="isInFeed" filterSetter={setIsInFeed} />
              </div>
            </div>

            {step === 'thoughts' && !thoughtsRetrieved && (
              <button
                className="btn-primary accept-btn"
                onClick={handleAcceptThoughts}
                disabled={loading}
              >
                {loading ? <Loader2 className="spinning" size={16} /> : null}
                Accept
              </button>
            )}

            {thoughtsRetrieved && (
              <div className="success-message">
                <CheckCircle size={16} />
                <span>Thoughts retrieved ({retrievedThoughts.length} thoughts)</span>
              </div>
            )}
          </div>
        </div>

        {/* Step 3: Simulation Parameters */}
        <div
          className={`step-card ${
            step === 'simulation' ? 'active' : simulationConfigured ? 'completed' : 'inactive'
          }`}
        >
          <div className="step-header">
            <div className="step-icon">
              {simulationConfigured ? <CheckCircle size={24} /> : <Settings size={24} />}
            </div>
            <h2>Step 3: Simulation Parameters</h2>
          </div>

          <div className="step-content">
            <div className="simulation-parameters">
              <div className="filter-group">
                <label>
                  Percentage chance of each user taking a position on a thought (1-100):
                </label>
                <input
                  type="number"
                  value={positionPercentage}
                  onChange={(e) => setPositionPercentage(e.target.value)}
                  disabled={simulationConfigured || step !== 'simulation'}
                  placeholder="75"
                  min="1"
                  max="100"
                />
              </div>
              <div className="filter-group">
                <label>Percentage chance of each user adding supporting beliefs (0-100):</label>
                <input
                  type="number"
                  value={supportingBeliefsPercentage}
                  onChange={(e) => setSupportingBeliefsPercentage(e.target.value)}
                  disabled={simulationConfigured || step !== 'simulation'}
                  placeholder="50"
                  min="0"
                  max="100"
                />
              </div>

              <div className="filter-group">
                <label>Contrarian position after X number of matching positions:</label>
                <input
                  type="number"
                  value={contrarianAfterMatches}
                  onChange={(e) => setContrarianAfterMatches(e.target.value)}
                  disabled={simulationConfigured || step !== 'simulation'}
                  placeholder="5"
                  min="1"
                />
              </div>
            </div>

            {step === 'simulation' && !simulationConfigured && (
              <button
                className="btn-primary accept-btn"
                onClick={handleAcceptSimulation}
                disabled={loading}
              >
                {loading ? <Loader2 className="spinning" size={16} /> : null}
                Accept
              </button>
            )}

            {simulationConfigured && (
              <div className="success-message">
                <CheckCircle size={16} />
                <span>Simulation parameters configured</span>
              </div>
            )}
          </div>
        </div>

        {/* Step 4: Ready */}
        {step === 'ready' && (
          <div className="step-card active">
            <div className="step-header">
              <div className="step-icon">
                <CheckCircle size={24} />
              </div>
              <h2>Ready for Processing</h2>
            </div>
            <div className="step-content">
              <p>Configuration complete:</p>
              <ul>
                <li>{retrievedUsers.length} users loaded</li>
                <li>{retrievedThoughts.length} thoughts loaded</li>
                <li>Simulation parameters configured</li>
              </ul>
              <div className="simulation-summary">
                <h4>Simulation Parameters:</h4>
                <ul>
                  <li>Position chance: {positionPercentage}%</li>
                  <li>Supporting beliefs chance: {supportingBeliefsPercentage}%</li>
                  <li>Contrarian after: {contrarianAfterMatches} matches</li>
                </ul>
              </div>
              <div className="auto-mode-container" style={{ marginBottom: '1rem' }}>
                <label
                  className="auto-checkbox-label"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <input
                    type="checkbox"
                    checked={autoMode}
                    onChange={(e) => setAutoMode(e.target.checked)}
                    className="auto-checkbox"
                  />
                  <span className="auto-checkbox-text">Auto Mode</span>
                </label>
                <p
                  className="auto-mode-description"
                  style={{ fontSize: '0.9em', color: '#6b7280', margin: 0 }}
                >
                  When enabled, automatically proceeds through steps. Manual review still required
                  for supporting beliefs.
                </p>
              </div>

              <div className="auto-mode-container" style={{ marginBottom: '1rem' }}>
                <label
                  className="auto-checkbox-label"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <input
                    type="checkbox"
                    checked={autoAcceptSupportingBeliefs}
                    onChange={(e) => setAutoAcceptSupportingBeliefs(e.target.checked)}
                    className="auto-checkbox"
                  />
                  <span className="auto-checkbox-text">Auto-accept supporting beliefs</span>
                </label>
                <p
                  className="auto-mode-description"
                  style={{ fontSize: '0.9em', color: '#6b7280', margin: 0 }}
                >
                  When enabled, automatically accepts all supporting beliefs without manual review.
                </p>
              </div>
              <button className="btn-primary accept-btn" onClick={startProcessing}>
                <ArrowRight size={16} />
                Start Processing
              </button>
            </div>
          </div>
        )}

        {/* Processing Step */}
        {step === 'processing' && (
          <div className="step-card active">
            <div className="step-header">
              <div className="step-icon">
                <Brain size={24} />
              </div>
              <h2>Processing AI Simulation</h2>
            </div>
            <div className="step-content">
              <div className="processing-status">
                <div className="processing-progress">
                  <p>
                    <strong>Progress:</strong> Thought {currentThoughtIndex + 1} of{' '}
                    {retrievedThoughts.length}, User {currentUserIndex + 1} of{' '}
                    {retrievedUsers.length}
                  </p>
                  <div className="belief-counts">
                    <span className="count-item believe">
                      Believe: {beliefPositionCounts.believe}
                    </span>
                    <span className="count-item disbelieve">
                      Disbelieve: {beliefPositionCounts.disbelieve}
                    </span>
                    <span className="count-item unsure">Unsure: {beliefPositionCounts.unsure}</span>
                  </div>
                </div>

                <div className="processing-message">
                  <p>{getProcessingMessage()}</p>
                </div>

                {processingStage === 'reviewing_supporting_beliefs' && (
                  <div className="supporting-beliefs-review-section">
                    <h4>Review Supporting Beliefs</h4>

                    {/* Context Information */}
                    <div className="belief-context">
                      <div className="context-section">
                        <h5>User Context</h5>
                        <div className="context-grid">
                          <div className="context-item">
                            <span className="context-label">Name:</span>
                            <span className="context-value">
                              {getCurrentUser()?.name || 'Unknown'}
                            </span>
                          </div>
                          <div className="context-item">
                            <span className="context-label">ID:</span>
                            <span className="context-value">
                              {getCurrentUser()?.account_id || 'Unknown'}
                            </span>
                          </div>
                          <div className="context-item">
                            <span className="context-label">Political:</span>
                            <span className="context-value">
                              {getCurrentUser()?.political || 'Unknown'}
                            </span>
                          </div>
                          <div className="context-item">
                            <span className="context-label">Religion:</span>
                            <span className="context-value">
                              {getCurrentUser()?.religion || 'Unknown'}
                            </span>
                          </div>
                          <div className="context-item">
                            <span className="context-label">Education:</span>
                            <span className="context-value">
                              {getCurrentUser()?.education || 'Unknown'}
                            </span>
                          </div>
                          <div className="context-item">
                            <span className="context-label">Location:</span>
                            <span className="context-value">
                              {getCurrentUser()?.city && getCurrentUser()?.state
                                ? `${getCurrentUser()?.city}, ${getCurrentUser()?.state}`
                                : getCurrentUser()?.country || 'Unknown'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="context-section">
                        <h5>Thought & Belief Context</h5>
                        <div className="context-content">
                          <div className="thought-context">
                            <span className="context-label">Thought:</span>
                            <p className="thought-text">{getCurrentThought()?.text || 'Unknown'}</p>
                          </div>
                          <div className="belief-context">
                            <span className="context-label">User's Position:</span>
                            <span
                              className={`belief-position-display ${
                                currentBelief?.position?.toLowerCase() || 'unknown'
                              }`}
                            >
                              {currentBelief?.position || 'Unknown'}
                            </span>
                          </div>
                          {currentBeliefSystem && (
                            <div className="belief-system-context">
                              <span className="context-label">Belief System:</span>
                              <p className="belief-system-text">
                                {currentBeliefSystem.belief_system}
                              </p>
                              <span className="reasoning-label">
                                Reasoning: {currentBeliefSystem.reasoning}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="review-instructions">
                      <p>Review the generated supporting beliefs in the context above. You can:</p>
                      <ul>
                        <li>
                          Click the position to toggle between BELIEVE, DISBELIEVE, and UNSURE
                        </li>
                        <li>Click the trash icon to remove a belief</li>
                      </ul>
                    </div>

                    {currentSupportingBeliefs.existing.length === 0 &&
                    currentSupportingBeliefs.new.length === 0 ? (
                      <p className="no-beliefs-message">No supporting beliefs generated.</p>
                    ) : (
                      <div className="supporting-beliefs-sections">
                        {/* Existing Supporting Beliefs */}
                        {currentSupportingBeliefs.existing.length > 0 && (
                          <div className="belief-section">
                            <h5 className="section-title">Existing Supporting Beliefs</h5>
                            <p className="section-description">
                              These beliefs already exist and can only be removed (position cannot
                              be changed).
                            </p>
                            <div className="supporting-beliefs-grid">
                              {currentSupportingBeliefs.existing.map((belief, index) => (
                                <div
                                  key={`existing-${index}`}
                                  className="supporting-belief-card existing"
                                >
                                  <div className="belief-content">
                                    <p className="belief-text">{belief.text}</p>
                                    <span
                                      className={`position-display ${belief.position.toLowerCase()}`}
                                    >
                                      {belief.position}
                                    </span>
                                  </div>
                                  <button
                                    className="remove-belief-btn"
                                    onClick={() => removeExistingSupportingBelief(index)}
                                    title="Remove this supporting belief"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* New Supporting Beliefs */}
                        {currentSupportingBeliefs.new.length > 0 && (
                          <div className="belief-section">
                            <h5 className="section-title">New Supporting Beliefs</h5>
                            <p className="section-description">
                              These are new beliefs that can be edited or removed.
                            </p>
                            <div className="supporting-beliefs-grid">
                              {currentSupportingBeliefs.new.map((belief, index) => (
                                <div key={`new-${index}`} className="supporting-belief-card new">
                                  <div className="belief-content">
                                    <p className="belief-text">{belief.text}</p>
                                    <button
                                      className={`position-toggle-btn ${belief.position.toLowerCase()}`}
                                      onClick={() => toggleNewSupportingBeliefPosition(index)}
                                      title="Click to toggle position"
                                    >
                                      {belief.position}
                                    </button>
                                  </div>
                                  <button
                                    className="remove-belief-btn"
                                    onClick={() => removeNewSupportingBelief(index)}
                                    title="Remove this supporting belief"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
                      {currentThoughtIndex < retrievedThoughts.length - 1 && (
                        <button
                          className="btn-primary accept-btn"
                          style={{ marginLeft: 8 }}
                          onClick={() => {
                            setCurrentThoughtIndex(currentThoughtIndex + 1);
                            setCurrentUserIndex(0);
                            setCurrentBelief(null);
                            setCurrentBeliefSystem(null);
                            setCurrentSupportingBeliefs({ existing: [], new: [] });
                            setProcessingStage('thought_intro');
                          }}
                        >
                          Next Thought
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <button
                  className="btn-primary accept-btn"
                  onClick={handleProcessingNext}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="spinning" size={16} /> : null}
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}
    </div>
  );
};

export default MassAIPowerUserAction;
